/**
 * FeatureFlagContext
 *
 * Provides feature flag resolution across the app.
 * Loads flags once on mount, caches in localStorage.
 * Integrates with user context for resolution.
 */
import { createContext, useContext, useState, useEffect, useCallback, ReactNode, useMemo } from 'react';
import { useAuth } from '@/modules/shared/contexts/AuthContext';
import {
  type FeatureFlag,
  type FlagResolutionContext,
  type FlagResolutionResult,
  resolveFlag,
  getVariant,
  getCachedFlags,
  setCachedFlags,
  clearFlagsCache,
  DEFAULT_FLAGS,
  enableDebugMode,
} from '@/modules/shared/utils/featureFlags';

// ─────────────────────────────────────────────────────────────────────────────
// Context Type
// ─────────────────────────────────────────────────────────────────────────────

interface FeatureFlagContextType {
  /** Check if a feature flag is enabled */
  isEnabled: (key: string) => boolean;
  /** Get full resolution result (enabled + source + variant) */
  resolve: (key: string) => FlagResolutionResult;
  /** Get A/B variant assignment for current user */
  getVariantFor: (key: string) => 'A' | 'B';
  /** All flags (for admin UI) */
  flags: Record<string, FeatureFlag>;
  /** Update a flag locally (admin use) */
  updateFlag: (key: string, updates: Partial<FeatureFlag>) => void;
  /** Refresh flags from source */
  refreshFlags: () => Promise<void>;
  /** Whether flags are still loading */
  isLoading: boolean;
}

const FeatureFlagCtx = createContext<FeatureFlagContextType | undefined>(undefined);

// ─────────────────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────────────────

export function FeatureFlagProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  // Initialize from cache or defaults
  const [flags, setFlags] = useState<Record<string, FeatureFlag>>(() => {
    return getCachedFlags() || DEFAULT_FLAGS;
  });
  const [isLoading, setIsLoading] = useState(false);

  // Build resolution context from auth
  const resolutionCtx: FlagResolutionContext = useMemo(() => ({
    userId: user?.userId || null,
    role: user?.programRoleName || user?.role || null,
    programId: user?.programId || null,
  }), [user?.userId, user?.programRoleName, user?.role, user?.programId]);

  // Load flags (from localStorage/API)
  const fetchFlags = useCallback(async () => {
    setIsLoading(true);
    try {
      // In production this would call: GET /api/feature-flags?programId=xxx
      // For now, use localStorage-persisted flags (admin can modify them)
      const cached = getCachedFlags();
      if (cached) {
        setFlags(cached);
      } else {
        // Fallback to defaults
        setFlags(DEFAULT_FLAGS);
        setCachedFlags(DEFAULT_FLAGS);
      }
    } catch {
      // On failure, use defaults but respect defaultOnFailure
      setFlags(DEFAULT_FLAGS);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  // Debug mode in development
  useEffect(() => {
    if (import.meta.env.DEV) {
      enableDebugMode(flags);
    }
  }, [flags]);

  // Resolution
  const isEnabled = useCallback((key: string): boolean => {
    const flag = flags[key];
    if (!flag) return false;
    return resolveFlag(flag, resolutionCtx).enabled;
  }, [flags, resolutionCtx]);

  const resolve = useCallback((key: string): FlagResolutionResult => {
    const flag = flags[key];
    if (!flag) return { enabled: false, source: 'default', variant: null };
    return resolveFlag(flag, resolutionCtx);
  }, [flags, resolutionCtx]);

  const getVariantFor = useCallback((key: string): 'A' | 'B' => {
    return getVariant(resolutionCtx.userId || 'anonymous', key);
  }, [resolutionCtx.userId]);

  // Admin: update a flag
  const updateFlag = useCallback((key: string, updates: Partial<FeatureFlag>) => {
    setFlags(prev => {
      const updated = {
        ...prev,
        [key]: { ...prev[key], ...updates, updatedAt: new Date().toISOString() },
      };
      setCachedFlags(updated);
      if (import.meta.env.DEV) enableDebugMode(updated);
      return updated;
    });
  }, []);

  const refreshFlags = useCallback(async () => {
    clearFlagsCache();
    await fetchFlags();
  }, [fetchFlags]);

  return (
    <FeatureFlagCtx.Provider
      value={{ isEnabled, resolve, getVariantFor, flags, updateFlag, refreshFlags, isLoading }}
    >
      {children}
    </FeatureFlagCtx.Provider>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Hooks
// ─────────────────────────────────────────────────────────────────────────────

export function useFeatureFlags() {
  const context = useContext(FeatureFlagCtx);
  if (context === undefined) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
  }
  return context;
}

/** Shorthand: check single flag */
export function useFeatureFlag(key: string): boolean {
  const { isEnabled } = useFeatureFlags();
  return isEnabled(key);
}

/** Shorthand: get variant for A/B test */
export function useFeatureVariant(key: string): { enabled: boolean; variant: 'A' | 'B' } {
  const { isEnabled, getVariantFor } = useFeatureFlags();
  return { enabled: isEnabled(key), variant: getVariantFor(key) };
}
